import type { FC, ReactNode } from 'react';
import { useId } from 'react';
import { Modal, Picture } from '@ui';
import icons from '@icons';
import { twcls } from '@utils/twcls';

const {
  info: { black: infoBlack },
  alertTriangle: { scarlet: alertTriangleScarlet },
  checkmarkCircle: { green: checkmarkCircleGreen },
  close: { midGrey: closeMidGrey }
} = icons.Outline;

export type ConfirmModalTone = 'info' | 'alarm' | 'success';

export interface ConfirmModalProps {
  open: boolean;
  title: ReactNode;
  text?: ReactNode;
  tone?: ConfirmModalTone;
  confirmText?: string;
  cancelText?: string;
  onConfirm: () => void;
  onClose: () => void;
}

const SIZE__CLS = 'w-[352px] sm:w-full';
const BOX__CLS = 'p-4 md:p-4 sm:p-4 rounded-2xl md:rounded-2xl sm:rounded-none';
const ICON_BOX__CLS =
  'flex items-center justify-center shrink-0 w-7 h-7 rounded';
const TITLE__CLS = 'text-base font-medium leading-5 [word-break:break-word]';
const TEXT__CLS =
  'text-sm leading-[18px] text-[var(--ultra-dark-grey)] [word-break:break-word]';
const CLOSE_BTN__CLS =
  'flex items-center justify-center shrink-0 w-6 h-6 p-0 border-0 bg-transparent cursor-pointer';
const BTN__CLS =
  'flex items-center justify-center flex-1 min-w-0 h-11 px-3 rounded-lg text-sm leading-[18px] text-center cursor-pointer transition-colors';
const CANCEL_BTN__CLS = twcls(
  BTN__CLS,
  'border border-solid border-[var(--ultra-dark-grey)] bg-transparent text-[#2b2b2b] hover:bg-[var(--light-grey)]'
);
const CONFIRM_BTN__CLS = twcls(BTN__CLS, 'border-0 text-white');

const INFO_ICON_BG__CLS = 'bg-[var(--light-grey)]';
const ALARM_ICON_BG__CLS = 'bg-[#fc7464]/20';
const SUCCESS_ICON_BG__CLS = 'bg-[#eaf7e0]';
const INFO_CONFIRM__CLS = 'bg-[var(--dark-red)] hover:bg-[var(--main-red)]';
const ALARM_CONFIRM__CLS = 'bg-[var(--scarlet)] hover:brightness-90';
const SUCCESS_CONFIRM__CLS = 'bg-[#80a82c] hover:brightness-90';

interface ToneStyle {
  icon: typeof infoBlack;
  iconBg: string;
  confirm: string;
}

const TONES: Record<ConfirmModalTone, ToneStyle> = {
  info: {
    icon: infoBlack,
    iconBg: INFO_ICON_BG__CLS,
    confirm: INFO_CONFIRM__CLS
  },
  alarm: {
    icon: alertTriangleScarlet,
    iconBg: ALARM_ICON_BG__CLS,
    confirm: ALARM_CONFIRM__CLS
  },
  success: {
    icon: checkmarkCircleGreen,
    iconBg: SUCCESS_ICON_BG__CLS,
    confirm: SUCCESS_CONFIRM__CLS
  }
};

const ConfirmModal: FC<ConfirmModalProps> = ({
  open,
  title,
  text,
  tone = 'info',
  confirmText = 'Подтвердить',
  cancelText = 'Отменить',
  onConfirm,
  onClose
}) => {
  const titleId = useId();
  const { icon, iconBg, confirm } = TONES[tone];

  return (
    <Modal
      open={open}
      onClose={onClose}
      framed
      withoutCross
      ariaLabelledby={titleId}
      size={SIZE__CLS}
      className={BOX__CLS}
      placementMobile='bottom-sheet'
    >
      <div className='flex flex-col gap-6'>
        <div className='flex flex-col gap-3'>
          <div className='flex items-start justify-between gap-3'>
            <div className='flex items-center min-w-0 gap-3'>
              <div className={twcls(ICON_BOX__CLS, iconBg)}>
                <Picture icon={icon} width='24' height='24' />
              </div>
              <div id={titleId} className={TITLE__CLS}>
                {title}
              </div>
            </div>
            <button
              type='button'
              aria-label='Закрыть'
              onClick={onClose}
              className={CLOSE_BTN__CLS}
            >
              <Picture icon={closeMidGrey} width='24' height='24' />
            </button>
          </div>
          {!!text && <div className={TEXT__CLS}>{text}</div>}
        </div>
        <div className='flex gap-3'>
          <button type='button' onClick={onClose} className={CANCEL_BTN__CLS}>
            {cancelText}
          </button>
          <button
            type='button'
            onClick={onConfirm}
            className={twcls(CONFIRM_BTN__CLS, confirm)}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default ConfirmModal;
